import React, { Component } from 'react';
import styled from 'styled-components';

import CountryLabel from '../../../../components/CountryLabel';
import ListRow from '../../../../components/List/Row';
import ListHeader from '../../../../components/List/Header';
import Button from '../../../../components/Button';
import Pellet from '../../../../components/Pellet';
import Card from '../../../../components/Card';
import CardHeader from '../../../../components/CardHeader';
import Pills from '../../../../components/Pills';
import ExportButton from '../../../../components/Export';
import Tooltip from '../../../../components/Tooltip';
import ContentTooltip from './ContentTooltip';
import get from 'lodash/get';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';
import CellStars from '../../../../components/Cell/CellStars';
import CellContact from '../../../../components/Cell/CellContact';

const CardContainer = styled.div`
  grid-column: ${(props) => (props.premium ? 'auto/span 2' : 'auto')};
`;

const PointsOfSalesListContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  color: ${(props) => props.theme.colors.dark};

  margin-top: ${(props) => (props.premium ? '30px' : '0px')};

  @media (max-width: 1600px) {
    margin-top: ${(props) => (props.premium ? '50px' : '0px')};
  }

  .titleContainer {
    display: flex;
    height: 100%;
    justify-content: flex-start;
    flex-direction: row;
    align-items: center;
    width: ${(props) =>
      props.premium ? 'calc(20% - 40px)' : 'calc(50% - 40px)'};
    padding-left: 40px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    line-height: 20px;
    text-decoration: none;
    color: ${(props) => props.theme.colors.dark};
  }
  .marketContainer {
    height: 100%;
    width: ${(props) => (props.premium ? '10%' : '25%')};
    justify-content: center;
  }

  .refContainer {
    font-size: 12px;
    height: 100%;
    width: ${(props) => (props.premium ? '10%' : '25%')};
    justify-content: center;
    text-align: center;
  }

  .refContainerBigger {
    width: 15%;
  }

  .showAllContainer {
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;
    width: 200px;
  }
`;

const PillPos = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  p {
    margin-left: 4px;
    white-space: nowrap;
  }
`;

class PointsOfSalesList extends Component {
  constructor(props) {
    super(props);

    let status = [
      {
        label: I18n.t('placeholderAll'),
        active: true,
        value: 'all'
      },
      {
        label: (
          <PillPos>
            <Pellet value="positive" />
            <p>{I18n.t('onlineSales.posPill')}</p>
          </PillPos>
        ),
        active: false,
        value: 'pos'
      }
    ];
    if (props.premium) {
      status.push(
        {
          label: (
            <PillPos>
              <Pellet value="negative" />
              <p>{I18n.t('onlineSales.lostPill')}</p>
            </PillPos>
          ),
          active: false,
          value: 'lost'
        },
        {
          label: (
            <PillPos>
              <Pellet value="neutral" />
              <p>{I18n.t('onlineSales.rivalPill')}</p>
            </PillPos>
          ),
          active: false,
          value: 'rival'
        }
      );
    }

    this.state = {
      showAll: false,
      sorted: null,
      desc: true,
      status: status
    };
  }

  filterData(data) {
    let filteredData = [...data];
    const { status } = this.state;
    const pillStatus = status.find((item) => item.active);

    if (pillStatus && pillStatus.value === 'pos') {
      return filteredData.filter((item) => item.nb_refs && item.nb_refs > 0);
    }
    if (pillStatus && pillStatus.value === 'rival') {
      return filteredData.filter((item) => item.nb_refs === 0);
    }
    if (pillStatus && pillStatus.value === 'lost') {
      return filteredData.filter((item) => item.is_lost === true);
    }

    return filteredData;
  }

  handleChange(value) {
    this.setState((state) => ({
      status: state.status.map((pill) => ({
        ...pill,
        active: pill.value === value
      }))
    }));
  }

  getListHeaderColumns() {
    const { premium } = this.props;
    const { desc } = this.state;
    return premium
      ? [
          {
            length: 'calc(20% - 20px)',
            padding: '20px',
            name: I18n.t('onlineSales.posColumns.pos'),
            sort: true,
            onSort: () => this.setState({ sorted: 'store_name', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.market'),
            length: '10%',
            sort: true,
            center: true,
            onSort: () =>
              this.setState({
                sorted: 'market_country_code',
                desc: !desc
              })
          },
          {
            name: I18n.t('onlineSales.posColumns.number'),
            length: '10%',
            sort: true,
            center: true,
            onSort: () => this.setState({ sorted: 'nb_refs', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.numberRival'),
            length: '10%',
            sort: true,
            center: true,
            onSort: () => this.setState({ sorted: 'nb_rivals', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.average_price'),
            length: '10%',
            sort: true,
            center: true,
            onSort: () => this.setState({ sorted: 'avg_price', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.total_offer'),
            length: '15%',
            sort: true,
            center: true,
            onSort: () => this.setState({ sorted: 'total_offer', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.stars'),
            length: '15%',
            sort: true,
            tooltip: {
              placement: 'top',
              content: I18n.t('onlineSales.tooltipRatingByWineservices')
            },
            center: true,
            onSort: () => this.setState({ sorted: 'stars', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.contact'),
            length: '10%',
            sort: false,
            center: true,
            onSort: () => this.setState({ sorted: 'nb_rivals', desc: !desc })
          }
        ]
      : [
          {
            length: 'calc(50% - 20px)',
            padding: '20px',
            name: I18n.t('onlineSales.posColumns.pos'),
            sort: true,
            onSort: () => this.setState({ sorted: 'store_name', desc: !desc })
          },
          {
            name: I18n.t('onlineSales.posColumns.market'),
            length: '25%',
            sort: true,
            center: true,
            onSort: () =>
              this.setState({
                sorted: 'market_country_code',
                desc: !desc
              })
          },
          {
            name: I18n.t('onlineSales.posColumns.number'),
            length: '25%',
            sort: true,
            center: true,
            onSort: () => this.setState({ sorted: 'refs_client', desc: !desc })
          }
        ];
  }

  sortColumn(data) {
    let { sorted, desc } = this.state;

    if (sorted === 'refs_client') {
      data.sort(
        (a, b) =>
          Number(a && a.refs_client ? a.refs_client.length : 0) -
          Number(b && b.refs_client ? b.refs_client.length : 0)
      );
    } else {
      data.sort(function (a, b) {
        if (!isNaN(a[sorted]) && !isNaN(b[sorted])) {
          return Number(a[sorted]) - Number(b[sorted]);
        }
        return String(a[sorted]).localeCompare(String(b[sorted]));
      });
    }

    if (desc) {
      return data;
    } else {
      return data.reverse();
    }
  }

  render() {
    const { data, retractedSize, premium, exportOptions } = this.props;

    const { showAll, status, sorted } = this.state;
    let filteredData = this.filterData(data);
    filteredData = sorted ? this.sortColumn(filteredData) : filteredData;

    let subSetData = showAll
      ? filteredData
      : filteredData.slice(0, retractedSize);
    const listHeaderColumns = this.getListHeaderColumns();
    return (
      <CardContainer premium={premium}>
        <Card className="card extraCard" id="vente-en-ligne-pdv-references">
          <CardHeader
            alignItems="center"
            title={
              <div className="alignComponent">
                <p>{I18n.t('onlineSales.posTitle')}</p>
                {premium && (
                  <div className="ignored-export">
                    <Pills
                      options={status}
                      onChange={({ value }) => this.handleChange(value)}
                      styles={`justify-content: space-between;`}
                    />
                  </div>
                )}
              </div>
            }
            styles={`.cardHeaderRight{width:100%; justify-content: flex-end;};flex-direction:row;align-items:center;`}
            fontSize="20px"
            iconRight="right"
            rightComponents={
              <ExportButton
                name={`${I18n.t('onlineSales.posTitle')}-${
                  get(exportOptions, 'market')
                    ? get(exportOptions, 'market.label')
                    : I18n.t('placeholderAll')
                }-${get(exportOptions, 'date')}-${get(
                  exportOptions,
                  'wine.label'
                )}`}
                jpg
                pdf
                xls
                path={`/web/premium/pos/xls?wines=["${get(
                  exportOptions,
                  'wine.label'
                )}"]${
                  get(exportOptions, 'market')
                    ? '&market=' + get(exportOptions, 'market.value')
                    : ''
                }`}
                newApi
                targetElement="#vente-en-ligne-pdv-references"
              />
            }
          />
          <PointsOfSalesListContainer premium={premium}>
            <ListHeader columns={listHeaderColumns} />
            {subSetData.map((pos, index) => (
              <ListRow
                dark={index % 2 === 0}
                key={`subscriptionRow + ${index}`}
                height="auto"
                padding="10px 0"
              >
                {pos.url ? (
                  <a
                    rel="noreferrer"
                    href={pos.url}
                    target="_blank"
                    className="titleContainer"
                  >
                    <p>{pos.store_name}</p>
                  </a>
                ) : (
                  <div className="titleContainer">
                    <p>{pos.store_name}</p>
                  </div>
                )}

                <div className="marketContainer">
                  {pos.market_country_code ? (
                    <CountryLabel
                      styleFlag={{ fontSize: 16 }}
                      name={pos.market_country_code}
                    />
                  ) : (
                    <p> - </p>
                  )}
                </div>
                {premium ? (
                  <div className="refContainer">
                    {pos.nb_refs && pos.nb_refs > 0 ? (
                      <Tooltip
                        placement="top"
                        content={
                          <ContentTooltip
                            title={I18n.t('tooltipWine')}
                            values={pos.refs_client.map(
                              (item) =>
                                `${item.wine_name} ${
                                  item.vintage && !isNaN(Number(item.vintage))
                                    ? ' - ' + item.vintage
                                    : ''
                                } (${item.format})`
                            )}
                          />
                        }
                      >
                        <p>{pos.nb_refs}</p>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        placement="top"
                        content={
                          <ContentTooltip
                            title=""
                            values={[
                              `${I18n.t('onlineSales.lastDateLabel')} ${
                                pos.last_date
                                  ? moment(pos.last_date).format('DD/MM/YYYY')
                                  : I18n.t('onlineSales.lastDateEmpty')
                              }`
                            ]}
                          />
                        }
                      >
                        <p>0</p>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <div className="refContainer">
                    {pos.refs_client && pos.refs_client.length > 0 ? (
                      <Tooltip
                        placement="top"
                        content={
                          <ContentTooltip
                            title={I18n.t('tooltipWine')}
                            values={pos.refs_client
                              .map(
                                (item) => `${item.wine_name} - ${item.vintage}`
                              )
                              .sort()}
                          />
                        }
                      >
                        <p>
                          {pos.refs_client.reduce(
                            (acc, base) => acc + base.refs,
                            0
                          )}
                        </p>
                      </Tooltip>
                    ) : (
                      <p> - </p>
                    )}
                  </div>
                )}
                {premium && (
                  <div className="refContainer">
                    {pos.nb_rivals && pos.nb_rivals > 0 ? (
                      <Tooltip
                        placement="top"
                        content={
                          <ContentTooltip
                            title={I18n.t('tooltipRivalWine')}
                            values={pos.rivals.sort()}
                          />
                        }
                      >
                        <p>{pos.nb_rivals}</p>
                      </Tooltip>
                    ) : (
                      <p> - </p>
                    )}
                  </div>
                )}
                {premium && (
                  <div className="refContainer">
                    {pos.avg_price && pos.avg_price > 0 ? (
                      <p>
                        {pos.avg_price.toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: pos.currency_code || 'EUR',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2
                        })}
                      </p>
                    ) : (
                      <p> - </p>
                    )}
                  </div>
                )}
                {premium && (
                  <div className="refContainer refContainerBigger">
                    {pos.total_offer && pos.total_offer > 0 ? (
                      <p>{pos.total_offer}</p>
                    ) : (
                      <p> - </p>
                    )}
                  </div>
                )}
                {premium && (
                  <div className="refContainer refContainerBigger">
                    {pos.stars && pos.stars > 0 ? (
                      <CellStars stars={pos.stars} nbStars={5} />
                    ) : (
                      <p> - </p>
                    )}
                  </div>
                )}
                {premium && (
                  <div className="refContainer">
                    <CellContact
                      value={{
                        site: pos.url,
                        phone: pos.phone,
                        email: pos.email,
                        address: pos.address
                      }}
                    />
                  </div>
                )}
              </ListRow>
            ))}
            <div className="showAllContainer ignored-export">
              {!showAll && filteredData.length > retractedSize && (
                <Button
                  width="200px"
                  label={`${I18n.t('seeMore')} (+${
                    filteredData.length - retractedSize
                  })`}
                  color="white"
                  textColor="black"
                  textTransform="normal"
                  border="0.8px black solid"
                  fontFamily="SFCompactText"
                  fontSize="12px !important"
                  onClick={() => this.setState({ showAll: true })}
                />
              )}
              {showAll && filteredData.length > retractedSize && (
                <Button
                  width="200px"
                  label={I18n.t('seeLess')}
                  color="white"
                  textColor="black"
                  textTransform="normal"
                  border="0.8px black solid"
                  fontFamily="SFCompactText"
                  fontSize="12px !important"
                  onClick={() => this.setState({ showAll: false })}
                />
              )}
            </div>
          </PointsOfSalesListContainer>
        </Card>
      </CardContainer>
    );
  }
}

export default PointsOfSalesList;
